import type { BusinessSeatBucket } from '../../packs/constants/pricingPlans.constants'
import type { AccountAdminCopy } from '../../i18n/translations/accountAdminCopy/accountAdminCopy.en'
import {
  WC2026_ORG_BILLING_DISCLOSURE,
  WC2026_ORG_BILLING_DISCLOSURE_SHORT,
} from '../../shared/constants/wc2026OrgBillingDisclosure.constants'

export const BILLING_COPY = {
  /** B2B organization tier (Starter / Business / …). */
  ORGANIZATION_PLAN_LABEL: 'Organization plan',
  /** Plan picker shown when org is on the FREE tier. */
  PLAN_PICKER_HEADING: 'Choose your plan',
  PLAN_PICKER_SUBHEADING:
    'One-time WC 2026 organization access — branding, member seats, and linked squad for the tournament period.',
  PLAN_BUSINESS_NAME: 'Business',
  PLAN_BUSINESS_PRICE: 'From $450',
  PLAN_BUSINESS_TAGLINE:
    'Self-serve one-time org fee. Pick your seat tier and pay once for WC 2026 tournament access.',
  /** Shown on Business plan card and before checkout in Plan & billing. */
  ORG_BILLING_DISCLOSURE: WC2026_ORG_BILLING_DISCLOSURE,
  ORG_BILLING_DISCLOSURE_SHORT: WC2026_ORG_BILLING_DISCLOSURE_SHORT,
  PLAN_BUSINESS_CTA: 'Get started',
  PLAN_ENTERPRISE_NAME: 'Enterprise',
  PLAN_ENTERPRISE_PRICE: 'Custom',
  PLAN_ENTERPRISE_TAGLINE:
    "Custom pricing for large organizations. Get in touch and we'll build a plan that fits.",
  PLAN_ENTERPRISE_CTA: 'Contact us',
  CUSTOM_FORM_NAME_LABEL: 'Your name',
  CUSTOM_FORM_NAME_PLACEHOLDER: 'Jane Smith',
  CUSTOM_FORM_EMAIL_LABEL: 'Work email',
  CUSTOM_FORM_EMAIL_PLACEHOLDER: 'jane@company.com',
  CUSTOM_FORM_MESSAGE_LABEL: 'Message (optional)',
  CUSTOM_FORM_MESSAGE_PLACEHOLDER: 'Tell us about your organization and what you need.',
  CUSTOM_FORM_SUBMIT: 'Send inquiry',
  CUSTOM_FORM_SENT: "We'll be in touch soon!",
  /** @deprecated No longer displayed in the billing tab. */
  PERSONAL_PLAN_LABEL: 'Personal plan',
  /** Shown when a negotiated Stripe Price is on the org but there is no org subscription row yet (or one-time Enterprise). */
  ENTERPRISE_QUOTE_LABEL: 'Enterprise agreed price',
  MANAGE_PORTAL: 'Manage organization billing',
  MANAGE_PERSONAL_PORTAL: 'Manage personal subscription',
  COMPARE_PLANS: 'Compare plans',
  SUBSCRIPTION_STATUS: 'Subscription status',
  PERIOD_END: 'Renews or period ends',
  PERSONAL_BILLING_SECTION: 'Personal subscription',
  ORG_BILLING_SECTION: 'Organization subscription',
  STRIPE_NOT_CONFIGURED:
    'Billing is being set up. Plan details will be available soon.',
  /** Shown after B2B Stripe checkout redirect while sync + account refresh run. */
  B2B_BILLING_RETURN_SYNCING: 'Applying your Stripe payment…',
  B2B_BILLING_RETURN_SYNCING_A11Y: 'Applying your Stripe payment',
  /** One-time Business plan — in-app seat tier upgrade. */
  ORG_SEAT_TIER_LABEL: 'Seat tier',
  /** Shown above the upgrade target picker so the active tier stays visible. */
  ORG_CURRENT_SEAT_TIER_LABEL: 'Current seat tier',
  UPGRADE_SEAT_TIER: 'Upgrade',
  /** Seat tier bump checkout — {@link buildOrgSeatTierUpgradeDialogMessage} for body. */
  SEAT_TIER_UPGRADE_DIALOG_TITLE: 'Upgrade seat tier?',
  SEAT_TIER_UPGRADE_DIALOG_CONFIRM: 'Continue to checkout',
  SEAT_TIER_UPGRADE_DIALOG_CANCEL: 'Cancel',
  SEAT_TIER_UPGRADE_DIALOG_CURRENT_PREFIX: 'Current seat tier:',
  SEAT_TIER_UPGRADE_DIALOG_TARGET_PREFIX: 'Upgrading to:',
  SEAT_TIER_UPGRADE_DIALOG_REFUND_BODY:
    'You already paid for your current (lower) seat tier. Stripe will credit or refund the unused portion of that charge toward this upgrade, so checkout reflects the net amount (proration).',
  SEAT_TIER_UPGRADE_DIALOG_SCOPE_REMINDER: WC2026_ORG_BILLING_DISCLOSURE_SHORT,
  SEAT_TIER_UPGRADE_DIALOG_CONTINUE_PROMPT: 'Continue?',
  NEED_CUSTOM_PLAN: 'Need a custom plan?',
  NEED_CUSTOM_PLAN_A11Y: 'Custom plan',
  PLAN_TIER_STARTER: 'Starter (Free)',
  PLAN_TIER_PRO: 'Pro',
  PLAN_TIER_BUSINESS: 'Business',
  PLAN_TIER_ENTERPRISE: 'Enterprise',
  SUBSCRIPTION_STATUS_ACTIVE: 'Active',
  SUBSCRIPTION_STATUS_TRIALING: 'Trialing',
  SUBSCRIPTION_STATUS_PAST_DUE: 'Past due',
  SUBSCRIPTION_STATUS_CANCELED: 'Canceled',
  SUBSCRIPTION_STATUS_UNPAID: 'Unpaid',
  SUBSCRIPTION_STATUS_INCOMPLETE: 'Incomplete',
  SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED: 'Expired',
  SUBSCRIPTION_STATUS_PAUSED: 'Paused',
} as const

/**
 * Confirms one-time Business seat tier bump before redirecting to Stripe checkout.
 */
export function buildOrgSeatTierUpgradeDialogMessage(
  current: BusinessSeatBucket,
  target: BusinessSeatBucket,
  billing: AccountAdminCopy['billing'] = BILLING_COPY,
): string {
  const currentLine = `${current.tagline} — ${current.priceLabel}`
  const targetLine = `${target.tagline} — ${target.priceLabel}`
  return [
    `${billing.SEAT_TIER_UPGRADE_DIALOG_CURRENT_PREFIX} ${currentLine}`,
    '',
    `${billing.SEAT_TIER_UPGRADE_DIALOG_TARGET_PREFIX} ${targetLine}`,
    '',
    billing.SEAT_TIER_UPGRADE_DIALOG_REFUND_BODY,
    '',
    billing.SEAT_TIER_UPGRADE_DIALOG_SCOPE_REMINDER,
    '',
    billing.SEAT_TIER_UPGRADE_DIALOG_CONTINUE_PROMPT,
  ].join('\n')
}
